import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import Logo from '../../../assets/svg/logo.svg';
import { ReactComponent as PayPal } from '../../../assets/svg/paypal.svg';
import { ReactComponent as Visa } from '../../../assets/svg/visa.svg';
import { ReactComponent as MasterCard } from '../../../assets/svg/mastercard.svg';
import { ReactComponent as ApplePay } from '../../../assets/svg/applepay.svg';
import { ReactComponent as GooglePay } from '../../../assets/svg/googlepay.svg';
import { ReactComponent as BankWhite } from '../../../assets/svg/bankwhite.svg';
import { ReactComponent as BankDark } from '../../../assets/svg/bankdark.svg';
import { Star } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import {setSettingsOpen} from '../../../store/slices/settingsDrawerSlice';

const languageFlags: Record<string, string> = {
  en: 'GB',
  de: 'DE',
  fr: 'FR',
  es: 'ES',
  br: 'BR',
  zh: 'CN',
  cs: 'CZ',
  hi: 'IN',
  id: 'ID',
  ja: 'JP',
  ko: 'KR',
  no: 'NO',
  pl: 'PL',
  ru: 'RU',
  se: 'SE',
  th: 'TH',
  tr: 'TR',
  ua: 'UA',
};

const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  AUD: '$',
  CAD: '$',
  CHF: 'CHF',
  CNY: '¥',
  SEK: 'kr',
  NZD: '$',
  BRL: 'R$',
  CZK: 'Kč',
  HRN: '₴',
  IDR: 'Rp',
  INR: '₹',
  KRW: '₩',
  NOK: 'kr',
  PLN: 'zł',
  RUB: '₽',
  THB: '฿',
  TRY: '₺',
};

const General = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const language = useSelector((state: RootState) => state.language.language);
  const currencyState = useSelector((state: RootState) => state.currency);

  const currentCurrency = typeof currencyState === 'object' && currencyState !== null
      ? (currencyState as any).currency
      : currencyState;

  const currentSymbol = currencySymbols[currentCurrency] || '';
  const currentCountryCode = languageFlags[language] || 'US';

  const isDarkMode = useSelector((state: RootState) => state.darkMode.isDarkMode);

  const handleOpenSettings = () => {
    dispatch(setSettingsOpen(true));
  };

  return (
      <div className="flex flex-col text-sm font-semibold text-neutral-500 dark:text-neutral-300 gap-2 mb-4 lg:mb-0">
        {/* Logo */}
        <div className="flex gap-2">
          <img src={Logo} alt="MoonSkins Logo" className="w-20" />
          <div>
            <div className="text-lg text-dark-primary">{t('footer.company')}.GG</div>
            <div>We ❤️ Skins</div>
            <div>to the 🌙 and back</div>
          </div>
        </div>

        {/* Trusted */}
        <a
            href="https://www.trustpilot.com/review/moonskins.gg"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 mb-4 hover:opacity-80 transition-opacity w-fit mt-4"
        >
        <span className="text-neutral-500 dark:text-neutral-300 font-bold mr-0.5 leading-none">
          Trustpilot
        </span>
          <div className="flex gap-0.5 items-center h-full">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="bg-[#00b67a] p-0.5 rounded-sm flex items-center justify-center w-5 h-5">
                  <Star className="text-white" fontSize="small" />
                </div>
            ))}
          </div>
        </a>

        {/* Payments */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Tooltip title="PayPal" arrow>
            <div className="h-9 w-9 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 rounded-lg p-1 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full">
              <PayPal />
            </div>
          </Tooltip>

          <Tooltip title="Mastercard" arrow>
            <div className="h-9 w-9 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 rounded-lg p-1 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full">
              <MasterCard />
            </div>
          </Tooltip>

          <Tooltip title="Visa" arrow>
            <div className="h-9 w-9 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 rounded-lg p-1 flex items-center justify-center text-[#1A1F71] dark:text-white [&>svg]:w-full [&>svg]:h-full [&>svg]:fill-current">
              <Visa />
            </div>
          </Tooltip>

          <Tooltip title="Apple Pay" arrow>
            <div className="h-9 w-9 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 rounded-lg p-1 flex items-center justify-center text-black dark:text-white [&>svg]:w-full [&>svg]:h-full [&>svg]:fill-current">
              <ApplePay />
            </div>
          </Tooltip>

          <Tooltip title="Google Pay" arrow>
            <div className="h-9 w-9 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 rounded-lg p-1 flex items-center justify-center text-[#757575] dark:text-white [&>svg]:w-full [&>svg]:h-full [&>svg]:fill-current">
              <GooglePay />
            </div>
          </Tooltip>

          <Tooltip title="Open Banking" arrow>
            <div className="h-9 w-9 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 rounded-lg p-1 flex items-center justify-center text-[#002D62] dark:text-white [&>svg]:w-full [&>svg]:h-full [&>svg]:fill-current">
              {isDarkMode ? <BankWhite /> : <BankDark />}
            </div>
          </Tooltip>
        </div>

        {/* Settings Button Box */}
        <Tooltip title={t('footer.settings') || 'Settings'} arrow>
          <button
              onClick={handleOpenSettings}
              className="flex items-center justify-start w-32 p-2 rounded-md border border-neutral-300 dark:border-neutral-700 bg-transparent hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer text-left gap-4"
          >
            <div className="flex items-center shrink-0">
              <ReactCountryFlag
                  svg
                  countryCode={currentCountryCode}
                  style={{ width: '1.5em', height: '1.5em' }}
              />
            </div>
            <div className="w-px h-4 bg-neutral-300 dark:bg-neutral-500 shrink-0" />
            <div className="text-neutral-500 dark:text-neutral-300 font-semibold truncate text-sm">
              {currentCurrency} ({currentSymbol})
            </div>
          </button>
        </Tooltip>
      </div>
  );
};

export default General;